import {
  PointToPointTransportCostBreakdownResultDTO,
  TransitTimeEstimateResultDTO,
  TransitTimeSourceExplanation,
} from 'api';
import { useFormContext } from 'form';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { Alert, Box, formatCurrency, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from 'ui';
import { HelpOutlineOutlined } from 'ui/icons';

import { displayTransportCostSource } from '../../../transport-costs/utils';
import { MissingCostsAlert } from '../MissingCostsAlert/MissingCostsAlert';

const sourceExplanationEnumValueToString = (
  enumValue: TransitTimeSourceExplanation,
  t: I18nTranslateFunction<string>
): string => {
  switch (enumValue) {
    case TransitTimeSourceExplanation.FULL_MATCH:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.fullMatch');
    case TransitTimeSourceExplanation.INCOTERMS_MISMATCH:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.incotermsMismatch');
    case TransitTimeSourceExplanation.ORIGIN_COUNTRY_MISMATCH:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.originCountryMismatch');
    case TransitTimeSourceExplanation.DESTINATION_COUNTRY_MISMATCH:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.destinationCountryMismatch');
    case TransitTimeSourceExplanation.BOTH_ORIGIN_AND_DESTINATION_COUNTRY_MISMATCH:
      return t(
        'profitabilityCalculator.transportCostDetails.sourceExplanations.bothOriginAndDestinationCountryMismatch'
      );
    case TransitTimeSourceExplanation.POL_MISMATCH:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.polMismatch');
    case TransitTimeSourceExplanation.POD_MISMATCH:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.podMismatch');
    case TransitTimeSourceExplanation.BOTH_POL_AND_POD_MISMATCH:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.bothPolAndPodMismatch');
    case TransitTimeSourceExplanation.CONTINENT_TO_CONTINENT_APPROXIMATION:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.continentToContinentApproximation');
    case TransitTimeSourceExplanation.ROAD_PER_KM_ESTIMATE:
      return t('profitabilityCalculator.transportCostDetails.sourceExplanations.roadPerKmEstimate');
    default:
      return '';
  }
};

export const TransportCostDetails = () => {
  const { t } = useTranslation('orders');
  const form = useFormContext();
  const {
    watch,
    formState: { errors },
  } = form;
  const tonnes = watch('tonnes');
  const tonnesPerLoad = watch('tonnes_per_load');
  const originAddress = watch('origin_address');
  const destinationAddress = watch('destination_address');
  const transportPrice: PointToPointTransportCostBreakdownResultDTO | undefined = watch('transport_price');
  const transitTimeEstimate: TransitTimeEstimateResultDTO | undefined = watch('transit_time_estimate');

  const displayedShippingCostPerLoad = formatCurrency(transportPrice?.total_cost_in_target_currency || 0, {
    currencyCode: transportPrice?.target_currency,
  });
  const displayedTotalShippingCost = formatCurrency(
    (Number.parseInt(transportPrice?.total_cost_in_target_currency || '0') * (tonnes || 0)) / (tonnesPerLoad || 1),
    {
      currencyCode: transportPrice?.target_currency,
    }
  );

  const unknown = t('profitabilityCalculator.transportCostDetails.values.notAvailable');
  const displayedOriginRoadCostSource = transportPrice?.origin_road_cost?.source
    ? displayTransportCostSource(transportPrice.origin_road_cost.source)
    : unknown;
  const displayedDestinationRoadCostSource = transportPrice?.destination_road_cost?.source
    ? displayTransportCostSource(transportPrice.destination_road_cost.source)
    : unknown;
  const displayedOceanFreightCostSource = transportPrice?.ocean_freight_cost?.source
    ? displayTransportCostSource(transportPrice.ocean_freight_cost.source)
    : unknown;
  const displayedDirectLandCostSource = transportPrice?.direct_land_cost?.source
    ? displayTransportCostSource(transportPrice.direct_land_cost.source)
    : unknown;

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        {t('profitabilityCalculator.transportCostDetails.title')}
      </Typography>
      {errors.transport_price ? <Alert severity="error">{String(errors.transport_price.message)}</Alert> : null}
      {errors.missing_transport_price ? (
        <Alert severity="warning">
          {t('profitabilityCalculator.transportCostDetails.alerts.noEstimatedRouteFound')}
        </Alert>
      ) : null}
      {errors.transit_time_estimate ? (
        <Alert severity="error">{String(errors.transit_time_estimate.message)}</Alert>
      ) : null}
      {errors.missing_transit_time_estimate ? (
        <Alert severity="warning">
          {t('profitabilityCalculator.transportCostDetails.alerts.noTransitTimeEstimateAvailable')}
        </Alert>
      ) : null}
      {transportPrice?.is_from_expired_quote ? (
        <Alert severity="error">
          {t('profitabilityCalculator.transportCostDetails.alerts.transportCostBasedOnExpiredQuote')}
        </Alert>
      ) : null}
      <MissingCostsAlert missingCosts={transportPrice?.missing_costs} />
      <Box
        sx={{
          marginTop: 1,
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('profitabilityCalculator.transportCostDetails.labels.transportCostPerLoad')}</TableCell>
              <TableCell data-testid="transport-estimator-shipping_cost_per_load">
                {displayedShippingCostPerLoad}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('profitabilityCalculator.transportCostDetails.labels.totalTransportCost')}</TableCell>
              <TableCell data-testid="transport-estimator-total-shipping-cost">{displayedTotalShippingCost}</TableCell>
            </TableRow>
            {originAddress && transportPrice?.origin_road_cost ? (
              <TableRow>
                <TableCell>{t('profitabilityCalculator.transportCostDetails.labels.originRoadCostSource')}</TableCell>
                <TableCell data-testid="transport-estimator-origin-road-cost-source">
                  {displayedOriginRoadCostSource}
                </TableCell>
                <TableCell>
                  {t('profitabilityCalculator.transportCostDetails.labels.quotedOn')}:{' '}
                  {transportPrice.origin_road_cost.quoted_on}
                </TableCell>
              </TableRow>
            ) : null}
            {destinationAddress && transportPrice?.destination_road_cost ? (
              <TableRow>
                <TableCell>
                  {t('profitabilityCalculator.transportCostDetails.labels.destinationRoadCostSource')}
                </TableCell>
                <TableCell data-testid="transport-estimator-destination-road-cost-source">
                  {displayedDestinationRoadCostSource}
                </TableCell>
                <TableCell>
                  {t('profitabilityCalculator.transportCostDetails.labels.quotedOn')}:{' '}
                  {transportPrice.destination_road_cost.quoted_on}
                </TableCell>
              </TableRow>
            ) : null}
            {originAddress && destinationAddress && transportPrice?.direct_land_cost ? (
              <TableRow>
                <TableCell>{t('profitabilityCalculator.transportCostDetails.labels.directLandCostSource')}</TableCell>
                <TableCell data-testid="transport-estimator-direct-land-cost-source">
                  {displayedDirectLandCostSource}
                </TableCell>
                <TableCell>
                  {t('profitabilityCalculator.transportCostDetails.labels.asOf')}:{' '}
                  {transportPrice.direct_land_cost.quoted_on}
                </TableCell>
              </TableRow>
            ) : null}
            {transportPrice?.ocean_freight_cost ? (
              <TableRow>
                <TableCell>{t('profitabilityCalculator.transportCostDetails.labels.oceanFreightCostSource')}</TableCell>
                <TableCell>{displayedOceanFreightCostSource}</TableCell>
                <TableCell>
                  {t('profitabilityCalculator.transportCostDetails.labels.quotedOn')}:{' '}
                  {transportPrice.ocean_freight_cost.quoted_on}
                </TableCell>
              </TableRow>
            ) : null}
            {transportPrice?.e2e_cost ? (
              <TableRow>
                <TableCell>{t('profitabilityCalculator.transportCostDetails.labels.costSource')}</TableCell>
                <TableCell>{displayTransportCostSource(transportPrice.e2e_cost.source)}</TableCell>
                <TableCell>
                  {t('profitabilityCalculator.transportCostDetails.labels.quotedOn')}:{' '}
                  {transportPrice.e2e_cost.quoted_on}
                </TableCell>
              </TableRow>
            ) : null}
            {transitTimeEstimate?.estimated_transit_days_collection_to_final_delivery ? (
              <TableRow>
                <TableCell sx={{ alignItems: 'center', display: 'flex' }}>
                  {t('profitabilityCalculator.transportCostDetails.labels.estimatedTransitTimeFull')}
                  <Tooltip title={t('profitabilityCalculator.transportCostDetails.tooltips.estimatedTransitTimeFull')}>
                    <Box component="span" sx={{ alignItems: 'center', display: 'flex', ml: 0.5 }}>
                      <HelpOutlineOutlined />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell data-testid="transport-estimator-estimated-transit-time-collection-to-final-delivery">
                  {transitTimeEstimate.estimated_transit_days_collection_to_final_delivery}{' '}
                  {t('profitabilityCalculator.transportCostDetails.values.days')}
                </TableCell>
              </TableRow>
            ) : null}
            {transitTimeEstimate?.estimated_transit_days_requested_route ? (
              <TableRow>
                <TableCell sx={{ alignItems: 'center', display: 'flex' }}>
                  {t('profitabilityCalculator.transportCostDetails.labels.estimatedTransitTimeRequested')}
                  <Tooltip
                    title={t('profitabilityCalculator.transportCostDetails.tooltips.estimatedTransitTimeRequested')}
                  >
                    <Box component="span" sx={{ alignItems: 'center', display: 'flex', ml: 0.5 }}>
                      <HelpOutlineOutlined />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell data-testid="transport-estimator-estimated-transit-time">
                  {transitTimeEstimate.estimated_transit_days_requested_route}{' '}
                  {t('profitabilityCalculator.transportCostDetails.values.days')}
                </TableCell>
              </TableRow>
            ) : null}
            {transitTimeEstimate?.source_description ? (
              <TableRow>
                <TableCell>{t('profitabilityCalculator.transportCostDetails.labels.transitTimeSource')}</TableCell>
                <TableCell sx={{ alignItems: 'center', display: 'flex' }}>
                  {transitTimeEstimate.source_description === TransitTimeSourceExplanation.FULL_MATCH
                    ? t('profitabilityCalculator.transportCostDetails.values.fullMatch')
                    : transitTimeEstimate.source_description ===
                        TransitTimeSourceExplanation.CONTINENT_TO_CONTINENT_APPROXIMATION
                      ? t('profitabilityCalculator.transportCostDetails.values.continentToContinentFallback')
                      : t('profitabilityCalculator.transportCostDetails.values.partialMatch')}
                  <Tooltip title={sourceExplanationEnumValueToString(transitTimeEstimate.source_description, t)}>
                    <Box component="span" sx={{ alignItems: 'center', display: 'flex', ml: 0.5 }}>
                      <HelpOutlineOutlined />
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
